import { useEffect, useState } from "react";

import { generateSubtitles, getSubtitleStatus, type SubtitleStatus } from "../../api/subtitles";
import { getProject } from "../../api/projects";
import { useEditor } from "../../store/editor";

const LANGUAGES = [
  { id: "auto", label: "Auto-detectar" },
  { id: "es", label: "Español" },
  { id: "en", label: "Inglés" },
  { id: "fr", label: "Francés" },
  { id: "pt", label: "Portugués" },
  { id: "it", label: "Italiano" },
  { id: "de", label: "Alemán" },
];

const STYLES = [
  { id: "subtle", label: "Clásico (caja negra)", desc: "Texto blanco sobre fondo semi-transparente" },
  { id: "beast", label: "Beast (impacto)", desc: "Sin fondo, letras enormes blancas" },
  { id: "simple", label: "Sencillo", desc: "Texto blanco sin caja" },
];

const MODELS = [
  { id: "tiny",   label: "Rápido (peor calidad)", note: "~5× tiempo real" },
  { id: "base",   label: "Equilibrado",            note: "~3× tiempo real" },
  { id: "small",  label: "Calidad (default)",      note: "~2× tiempo real" },
  { id: "medium", label: "Alta calidad",           note: "≈ tiempo real" },
];

export default function SubtitlesPanel({ projectId }: { projectId: number }) {
  const setProject = useEditor((s) => s.setProject);
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState("auto");
  const [style, setStyle] = useState("subtle");
  const [model, setModel] = useState("small");
  const [replace, setReplace] = useState(true);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [status, setStatus] = useState<SubtitleStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Polling de estado
  useEffect(() => {
    if (!taskId) return;
    let alive = true;
    let timer: number | null = null;
    async function tick() {
      try {
        const s = await getSubtitleStatus(taskId!);
        if (!alive) return;
        setStatus(s);
        if (s.status === "done") {
          const p = await getProject(projectId);
          setProject(p.id, p.timeline_json!);
          setBusy(false);
          // mantenemos el panel abierto unos segundos para mostrar el éxito
          timer = window.setTimeout(() => { setTaskId(null); setStatus(null); }, 4000);
          return;
        }
        if (s.status === "failed") {
          setError(s.error || "Error al transcribir");
          setBusy(false);
          return;
        }
        timer = window.setTimeout(tick, 1500);
      } catch (e: any) {
        if (!alive) return;
        setError(e?.message || String(e));
        setBusy(false);
      }
    }
    tick();
    return () => { alive = false; if (timer) window.clearTimeout(timer); };
  }, [taskId, projectId, setProject]);

  async function onGenerate() {
    setError(null);
    setBusy(true);
    setStatus({ status: "queued", progress: 0 });
    try {
      const { task_id } = await generateSubtitles({
        project_id: projectId,
        language: lang as any,
        style_preset: style as any,
        model_size: model as any,
        replace,
      });
      setTaskId(task_id);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Error");
      setBusy(false);
    }
  }

  return (
    <div style={{ marginTop: 12 }}>
      {!open && (
        <button onClick={() => setOpen(true)} className="primary"
                style={{ width: "100%", padding: 10 }}>
          ✨ Subtítulos automáticos
        </button>
      )}

      {open && (
        <div style={{
          marginTop: 6, padding: 12,
          background: "var(--bg-elev-2)",
          border: "1px solid var(--border)", borderRadius: 6,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <strong style={{ fontSize: 13 }}>✨ Subtítulos automáticos</strong>
            {!busy && (
              <button onClick={() => setOpen(false)} style={{ fontSize: 10, padding: "1px 6px" }}>×</button>
            )}
          </div>

          {!taskId && (
            <>
              <label className="label" style={{ marginTop: 10 }}>Idioma</label>
              <select value={lang} onChange={(e) => setLang(e.target.value)}>
                {LANGUAGES.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
              </select>

              <label className="label" style={{ marginTop: 8 }}>Estilo</label>
              {STYLES.map((s) => (
                <label key={s.id} style={{
                  display: "block", padding: "6px 8px", borderRadius: 4, cursor: "pointer", fontSize: 12,
                  background: style === s.id ? "rgba(91,199,255,0.15)" : "transparent",
                  border: style === s.id ? "1px solid var(--accent)" : "1px solid transparent",
                  marginBottom: 4,
                }}>
                  <input type="radio" name="style" value={s.id}
                         checked={style === s.id}
                         onChange={() => setStyle(s.id)}
                         style={{ width: "auto", marginRight: 6 }} />
                  <strong>{s.label}</strong>
                  <div style={{ marginLeft: 22, color: "var(--text-dim)", fontSize: 10 }}>{s.desc}</div>
                </label>
              ))}

              <label className="label" style={{ marginTop: 8 }}>Modelo</label>
              <select value={model} onChange={(e) => setModel(e.target.value)}>
                {MODELS.map((m) => <option key={m.id} value={m.id}>{m.label} — {m.note}</option>)}
              </select>

              <label style={{ display: "block", marginTop: 10, fontSize: 12 }}>
                <input type="checkbox" checked={replace}
                       onChange={(e) => setReplace(e.target.checked)}
                       style={{ width: "auto", marginRight: 6 }} />
                Reemplazar subtítulos automáticos anteriores
              </label>

              {error && <div className="error-text">{error}</div>}

              <button onClick={onGenerate} className="primary"
                      style={{ width: "100%", marginTop: 12 }} disabled={busy}>
                Generar
              </button>

              <p style={{ marginTop: 10, color: "var(--text-dim)", fontSize: 11 }}>
                Usa el primer clip con audio del timeline.
                {" "}Para vídeos largos (10+ min) puede tardar varios minutos.
              </p>
            </>
          )}

          {taskId && status && (
            <div style={{ marginTop: 12 }}>
              {status.status === "done" ? (
                <>
                  <div style={{ color: "var(--ok)", fontSize: 13 }}>
                    ✅ {status.result?.added} subtítulos generados
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-dim)", marginTop: 4 }}>
                    Idioma: {status.result?.language} ·
                    {" "}{status.result?.duration_s.toFixed(1)}s audio ·
                    {" "}{status.result?.elapsed_s}s proceso
                  </div>
                </>
              ) : status.status === "failed" ? (
                <div style={{ color: "var(--danger)", fontSize: 12 }}>
                  ❌ {status.error || "Error"}
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 12, marginBottom: 6 }}>
                    {status.stage === "loading_model" ? "Cargando modelo…"
                      : status.stage === "transcribing" ? "Transcribiendo…"
                      : "En cola…"}
                    {status.segments_so_far ? ` (${status.segments_so_far} segmentos)` : ""}
                  </div>
                  <div style={{
                    background: "var(--bg)", borderRadius: 4, height: 8, overflow: "hidden",
                  }}>
                    <div style={{
                      background: "var(--accent)", height: "100%",
                      width: `${status.progress || 0}%`,
                      transition: "width 0.3s",
                    }} />
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-dim)", marginTop: 4, textAlign: "right" }}>
                    {status.progress || 0}%
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
